// shared-lib/cabinet-runs/ovenColumn.js
//
// An oven column is THREE objects, not one box: `buildOvenHousing`
// (functions/features/post-processing/kitchen.js) cuts a floor-to-ceiling
// column at the oven's underside and again at its top, so the appliance sits
// in a surround between a base cabinet and an over-oven cabinet rather than
// behind one pair of floor-to-ceiling doors.
//
// This module is the ONE place that says which objects are one column and how
// big that column is. It is a LEAF — it requires nothing else from
// `cabinet-runs` — so the deriver (`scanRuns.js`), the incumbent pass
// (`incumbents.js`), the footprint pass (`bayFootprint.js`) and the web panel
// can all share one answer and cannot drift (the RP13 discipline from #1838:
// the panel asks the engine, it never re-derives).
//
// Pure. Nothing here mutates what it is given.
'use strict';

const IN = 0.0254;

/** The suffix `buildOvenHousing` appends for the two non-base sections. */
const PART_SUFFIX_RE = /-oven-(surround|upper)$/;

/** The vertical order of a column's sections, lowest first. */
const PART_ORDER = ['base', 'surround', 'upper'];

/**
 * Never let a squeezed column's surround panels collapse to nothing (or
 * negative). `buildOvenHousing` (`functions/features/post-processing/kitchen.js`)
 * clamps to this at BUILD time; the incumbent pass (`incumbents.emitOvenColumns`)
 * must clamp to the SAME floor whenever a re-solve narrows the column — a
 * non-scalable product pinning the column to a width narrower than its
 * current one is a live path, not a hypothetical one. One definition, so a
 * column fact lives where the rest of this module's column facts live rather
 * than a second constant drifting in `functions/` (which shared-lib may not
 * import from anyway).
 */
const MIN_SURROUND_PANEL = 0.25 * IN;

/**
 * Type strings a scanned oven APPLIANCE itself carries — never a column
 * section (`ovenHousing` is never set on it). `wall-oven`/`wallOven` are
 * `wallToRun`'s own vocabulary for the same appliance; a bare `oven` is what
 * every real scan emits.
 */
const OVEN_TYPE_RE = /^(oven|wall-oven|wallOven)$/i;

/** Is this object one section of an oven column? The producer's own flag. */
function isOvenColumnPart(o) {
  return !!o && o.ovenHousing === true;
}

/**
 * Is `o` a candidate oven — `ovenInColumn`'s search list, not a column
 * section. Room-wide: an oven is never a `SCAN_RUN_MEMBER_TYPE` and never
 * `ovenHousing`, so nothing upstream has already filtered it down for us.
 */
function isOvenCandidate(o) {
  return (
    !!o &&
    OVEN_TYPE_RE.test(String(o.type || '')) &&
    !!o.center &&
    !!o.dimensions
  );
}

/**
 * The id every section of one column shares.
 *
 * `buildOvenHousing` names the base after the widest cabinet it absorbed and
 * the other two `<that id>-oven-surround` / `-oven-upper`, so stripping the
 * suffix recovers the root — and it is still the root when the base section
 * was never emitted (an oven sitting on the floor), where no object carries
 * the bare root id at all.
 */
function columnRootId(o) {
  if (!isOvenColumnPart(o) || !o.id) return null;
  return String(o.id).replace(PART_SUFFIX_RE, '');
}

/**
 * Every oven column in `objects`, keyed by root id.
 * @returns {Map<string, {root: string, parts: Object[], base: Object|null,
 *   surround: Object|null, upper: Object|null}>}
 */
function groupOvenColumns(objects) {
  const byRoot = new Map();
  for (const o of objects || []) {
    const root = columnRootId(o);
    if (!root) continue;
    if (!byRoot.has(root)) byRoot.set(root, []);
    byRoot.get(root).push(o);
  }
  const out = new Map();
  for (const [root, found] of byRoot) {
    const parts = [...found].sort(
      (a, b) =>
        PART_ORDER.indexOf(a.ovenHousingPart) -
        PART_ORDER.indexOf(b.ovenHousingPart)
    );
    const at = (part) => parts.find((p) => p.ovenHousingPart === part) || null;
    out.set(root, {
      root,
      parts,
      base: at('base'),
      surround: at('surround'),
      upper: at('upper'),
    });
  }
  return out;
}

/**
 * The span the whole column occupies: the widest and deepest of its sections,
 * and the full floor-to-top height.
 *
 * The height is the point of this function. The bay used to take the NAMED
 * object's height, which is the base band — 18.1" to 37.7" across the corpus —
 * so a full-height column declared itself shorter than a base cabinet and no
 * filler beside one ever rose to meet it.
 *
 * `null` when no section carries a usable size: a column we cannot measure is
 * not a column of a default size (CLAUDE.md — no arbitrary fallbacks).
 */
function columnFootprint(column) {
  const parts = (column?.parts || []).filter((p) => p?.dimensions && p?.center);
  if (!parts.length) return null;
  const width = Math.max(...parts.map((p) => p.dimensions.x ?? 0));
  const depth = Math.max(...parts.map((p) => p.dimensions.z ?? 0));
  const bottomY = Math.min(
    ...parts.map((p) => p.center.y - (p.dimensions.y ?? 0) / 2)
  );
  const topY = Math.max(
    ...parts.map((p) => p.center.y + (p.dimensions.y ?? 0) / 2)
  );
  if (!(width > 0) || !(topY > bottomY)) return null;
  return { width, depth, height: topY - bottomY, bottomY, topY };
}

/**
 * The oven standing inside `column`: the appliance whose band the surround
 * section covers AND whose centre sits within the surround's own width of it.
 *
 * Vertical overlap ALONE is not enough — a kitchen may hold two wall ovens at
 * the same standard installation height (this file's own fixture height,
 * 37.7"/61.5"/109.5", is one such height), and `objects` is typically a
 * ROOM-WIDE oven candidate list (`isOvenCandidate`), so a vertical-only test
 * picked up whichever candidate a `.find()` happened to reach first for EVERY
 * column in the room, not just its own. The horizontal test is what actually
 * discriminates.
 *
 * `objects` is a candidate list of oven-typed objects, not the column's own
 * `parts` — an oven is the appliance the housing was built AROUND, never a
 * housing part itself (`ovenHousing` is never set on it), so it can never
 * appear in `groupOvenColumns`' output and has to be found separately.
 *
 * No threshold constant: the discriminator is the surround's OWN geometry.
 * `buildOvenHousing` re-centres the oven on the finished column ("Centre the
 * oven in the column it now sits in"), so a column's own oven sits almost
 * exactly on the surround's centre in the XZ plane — while a DIFFERENT
 * column's oven, whether on the same wall or an entirely different one, is at
 * least that column's own width away (adjacent columns are always at least
 * their own widths apart). Distance is a straight XZ line rather than a
 * along-one-wall projection on purpose: two columns on PARALLEL walls (a
 * galley, opposite sides of an island run) can share an along-wall
 * coordinate while sitting on opposite sides of the room, where only the
 * perpendicular component actually separates them. Among candidates passing
 * both tests, the nearest wins.
 *
 * `null` when the column has no surround (it always does — `buildOvenHousing`
 * pushes the surround unconditionally), the oven object was deleted, or no
 * candidate passes both tests. A `null` result is a column the run positions
 * without an appliance inside, which is a fine thing to be; it is not a
 * reason to refuse the column.
 *
 * Shared by both callers that need to find "the oven in THIS column" rather
 * than trust a stored id: `scanRuns.js` deriving a fresh anchor off the raw
 * scan, and `incumbents.js` healing a STALE bay that predates
 * `bay.column.ovenId` (Correction 4, for the appliance instead of the base
 * cabinet — see `emitOvenColumns`).
 */
function ovenInColumn(column, objects) {
  const s = column?.surround;
  if (!s?.center || !s?.dimensions) return null;
  const sBottom = s.center.y - s.dimensions.y / 2;
  const sTop = s.center.y + s.dimensions.y / 2;
  const halfWidth = s.dimensions.x / 2;
  let best = null;
  let bestDist = Infinity;
  for (const o of objects || []) {
    if (!o?.center || !o?.dimensions) continue;
    const b = o.center.y - o.dimensions.y / 2;
    const t = o.center.y + o.dimensions.y / 2;
    if (!(b >= sBottom - 1e-3 && t <= sTop + 1e-3)) continue;
    const dist = Math.hypot(o.center.x - s.center.x, o.center.z - s.center.z);
    if (dist > halfWidth + 1e-3) continue;
    if (dist < bestDist) {
      bestDist = dist;
      best = o;
    }
  }
  return best;
}

/**
 * Is this BAY an oven column's? A `tallUnit` bay carrying a column spec.
 *
 * The spec is what tells `emitTallUnits` to leave the bay alone and
 * `emitOvenColumns` to take it, and what tells the panel to read the bay as a
 * column rather than as a pantry (or, as it did before this change, as a plain
 * Doors bay with a width editor that silently did nothing).
 */
function isOvenColumnBay(bay) {
  return (
    !!bay &&
    bay.kind === 'tallUnit' &&
    !!bay.column &&
    Array.isArray(bay.column.partIds) &&
    bay.column.partIds.length > 0
  );
}

/**
 * Does `bay` resolve to an oven column, given the object it currently seats?
 *
 * This is THE shared discriminator: `emitTallUnits`'s disjointness guard
 * (`isReseatedTallUnit`), `emitOvenColumns`'s heal-on-read
 * (`ovenColumnPartsForBay`), and the web panel (`typeOfBay`,
 * `hasCabinetBayControls`) all have to agree on the same answer for the same
 * bay, or the run and the panel end up holding contradictory opinions about
 * it — which is exactly what happened when the panel used `isOvenColumnBay`
 * alone (#1838 follow-up). Two signals, either sufficient on its own:
 *
 *   - `isOvenColumnBay(bay)` — the bay itself carries `bay.column`, produced
 *     by this branch's deriver.
 *   - `isOvenColumnPart(object)` — `object` (whatever this bay currently
 *     seats — its `applianceId`, or the caller's own resolution) already
 *     carries the producer's `ovenHousing: true` flag. This is the heal for a
 *     STALE bay: one persisted before this branch, naming only the base
 *     cabinet via `applianceId`, with no `column` key at all. Every dev room
 *     is this shape until it is reprocessed. Drop this arm and a stale bay's
 *     column reads as a plain tall unit (or, in the panel, falls all the way
 *     through to `'standard'` — "Doors") until a reprocess, AND
 *     `emitTallUnits` would re-seat the stale bay's base cabinet ALONE at the
 *     bay's width, tearing it away from its own surround and over-oven
 *     cabinet on an ordinary web edit.
 *
 * Every section of a real column carries `ovenHousing: true` — not just the
 * base — so this answers the same for whichever of the three sections
 * `object` is, with no need to first resolve "the object this bay names".
 */
function bayResolvesToOvenColumn(bay, object) {
  return isOvenColumnBay(bay) || isOvenColumnPart(object);
}

/**
 * The column's live objects for `bay`, lowest section first. Ids that name
 * nothing are skipped — a part deleted by hand must not stop the rest of the
 * column being re-emitted.
 *
 * The lowest-first order is enforced HERE, by `PART_ORDER`, rather than
 * trusted from `bay.column.partIds` — this module is the one place that
 * defines a column, so the guarantee its docstring makes can't depend on the
 * order a different task happens to write the ids in.
 */
function columnPartsFor(bay, objects) {
  if (!isOvenColumnBay(bay)) return [];
  const byId = new Map(
    (objects || []).filter((o) => o?.id).map((o) => [o.id, o])
  );
  const parts = bay.column.partIds.map((id) => byId.get(id)).filter(Boolean);
  return parts.sort(
    (a, b) =>
      PART_ORDER.indexOf(a.ovenHousingPart) -
      PART_ORDER.indexOf(b.ovenHousingPart)
  );
}

/**
 * The oven column's live sections for `bay`, lowest first, AND the id of the
 * appliance standing inside it — or `null` when `bay` names no oven column at
 * all (an ordinary tall unit — a pantry — which is neither `emitTallUnits`'s
 * heal nor `bayFootprint`'s to derive from).
 *
 * THE two-armed resolver: `bayResolvesToOvenColumn` answers "is this a
 * column?" as a boolean, and this answers "which sections, and which oven?"
 * by the same two routes, so a caller that needs the sections never has to
 * re-derive the boolean's logic to get them:
 *
 *   - a bay carrying `bay.column` (`isOvenColumnBay`) reads its `partIds`
 *     and `ovenId` directly (`columnPartsFor` / `bay.column.ovenId`);
 *   - a STALE `tallUnit` bay — persisted before this branch, naming only the
 *     base cabinet via `applianceId`, with no `column` key at all — is
 *     recognised by the named object's own `ovenHousing` flag, and the rest
 *     of the column is found the way `groupOvenColumns` finds it anywhere
 *     else: from the shared id-suffix convention. This is the same
 *     heal-at-the-point-of-use pattern `stampApplianceBays` already uses for
 *     an un-stamped bay — no persisted migration, no reprocess requirement
 *     (correction 4). The oven has no stored id to read here either, so it is
 *     healed the SAME geometric way a fresh scan anchor finds it
 *     (`ovenInColumn`) — without this, the heal moves the three sections and
 *     leaves the appliance exactly where it stood, the identical tear-apart
 *     correction 4 exists to prevent, just for the appliance instead of the
 *     base cabinet.
 *
 * `parts: []` (not a `null` result) means "this IS an oven column bay, but
 * every section it named is gone" — the caller's cue to warn (`emitOvenColumns`)
 * or to treat the bay as dangling (`resolveRunBayFootprints`) rather than
 * silently keep whatever it last measured. `ovenId: null` is always a fine
 * thing to be: a column the run positions without an appliance inside it.
 *
 * Shared by `incumbents.js` (`emitOvenColumns`, which needs `ovenId` to
 * re-centre the appliance) and `bayFootprint.js` (which needs only `parts`,
 * and simply ignores `ovenId` — neither consumer has to re-derive the
 * other's half). Moved here from `incumbents.js` (originally
 * `ovenColumnPartsForBay`) once `bayFootprint.js` needed the same heal:
 * `isOvenColumnBay(bay)` alone left a stale bay's footprint reading the base
 * band (18-38") instead of the whole column, one arm short of the same bug
 * `isReseatedTallUnit` and the panel already had.
 *
 * @returns {{parts: Object[], ovenId: string|null}|null}
 */
function ovenColumnPartsForBay(bay, objects) {
  if (isOvenColumnBay(bay)) {
    return {
      parts: columnPartsFor(bay, objects),
      ovenId: bay.column.ovenId ?? null,
    };
  }
  if (bay?.kind !== 'tallUnit' || !bay.applianceId) return null;
  // The SAME lookup `emitTallUnits` uses for `bay.applianceId` (a `Map`, last
  // duplicate wins) — the whole disjointness argument between the passes
  // rests on both resolving one bay to the same object.
  const byId = new Map(
    (objects || []).filter((o) => o?.id).map((o) => [o.id, o])
  );
  const named = byId.get(bay.applianceId);
  if (!isOvenColumnPart(named)) return null;
  const root = columnRootId(named);
  const column = root ? groupOvenColumns(objects).get(root) : null;
  const oven = column
    ? ovenInColumn(column, (objects || []).filter(isOvenCandidate))
    : null;
  return { parts: column?.parts || [], ovenId: oven?.id ?? null };
}

module.exports = {
  isOvenColumnPart,
  isOvenCandidate,
  OVEN_TYPE_RE,
  columnRootId,
  groupOvenColumns,
  columnFootprint,
  ovenInColumn,
  isOvenColumnBay,
  bayResolvesToOvenColumn,
  columnPartsFor,
  ovenColumnPartsForBay,
  PART_ORDER,
  MIN_SURROUND_PANEL,
};
